import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CollectionNames,
  ProjectPermitTaskServiceCreateResponseJson,
  ProjectPermitTaskServiceCreateResponseSchema,
  ProjectPermitTaskServiceListRequestJson,
  ProjectPermitTaskServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import {
  PermitTaskRepositoryService,
  withSearchSync,
} from '../../repositories';
import { UtilitiesFacade } from '../utilities/utilities.facade';
import { PaginatedPermitTaskActions } from './paginated-permit-task.actions';

@Injectable()
export class PaginatedPermitTaskEffects extends AuthRunEffects {
  private readonly injector = inject(EnvironmentInjector);
  private readonly errors = inject(GlobalErrorHandlerService);
  private readonly repository = inject(PermitTaskRepositoryService);
  private readonly notifications = inject(NbToastrService);
  private readonly utilities = inject(UtilitiesFacade);

  listPaginatedPermitTasks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PaginatedPermitTaskActions.listPaginatedPermitTasks,
        PaginatedPermitTaskActions.cancel,
      ),
      switchMap((action) =>
        action.type === PaginatedPermitTaskActions.cancel.type
          ? EMPTY
          : of(action).pipe(this.performSearch()),
      ),
    );
  });

  performSearch() {
    return (
      source: Observable<{
        params: ProjectPermitTaskServiceListRequestJson;
      }>,
    ) =>
      source.pipe(
        debounceTime(250),
        withSearchSync(CollectionNames.Permits, this.injector),
        tap(() => this.utilities.queue()),
        switchMap((payload) => {
          return this.repository.list(
            fromJson(ProjectPermitTaskServiceListRequestSchema, payload.params),
          );
        }),
        tap((data) => {
          if (data.degraded) {
            this.errors.reportSearchDegradation(CollectionNames.Permits);
            this.notifications.warning(
              'There was an error with the search server for fetching permit tasks.',
              'Search',
              {
                duration: 5000,
              },
            );
          }
        }),
        map((data) =>
          PaginatedPermitTaskActions.listPaginatedPermitTasksSuccess({
            data: {
              ...data,
              items: (data.items ?? []).map(
                (item) =>
                  toJson(
                    ProjectPermitTaskServiceCreateResponseSchema,
                    item,
                    defaultApiSerializeJsonOptions,
                  ) as ProjectPermitTaskServiceCreateResponseJson,
              ),
            },
          }),
        ),
        catchError((error) => {
          this.errors.reportCaughtError(error);
          return of(
            PaginatedPermitTaskActions.listPaginatedPermitTasksFailure(error),
          );
        }),
        tap(() => this.utilities.complete()),
      );
  }

  constructor() {
    super(inject(Actions));
  }
}
